// biome-ignore-all lint/style/noMagicNumbers: values are aspect ratios

export type SocialMediaFormat = {
  id: string;
  chipLabel?: string;
  /** Sentence-case name sent to the agent and used for canvas labels. */
  label: string;
  ratio: string;
  value: number;
};

export type SocialMediaPlatformGroup = {
  id: string;
  label: string;
  formats: SocialMediaFormat[];
};

/** Platform order: typical perf-ad workflow (Meta → short video → YouTube → LinkedIn → X). */
export const SOCIAL_MEDIA_PLATFORM_GROUPS: SocialMediaPlatformGroup[] = [
  {
    formats: [
      { chipLabel: 'Portrait', id: 'instagram_feed_portrait', label: 'Feed Portrait', ratio: '4:5', value: 4 / 5 },
      { chipLabel: 'Square', id: 'instagram_feed_square', label: 'Feed Square', ratio: '1:1', value: 1 },
      {
        chipLabel: 'Landscape',
        id: 'instagram_feed_landscape',
        label: 'Feed Landscape',
        ratio: '1.91:1',
        value: 1.91,
      },
      { chipLabel: 'Reel', id: 'instagram_reel', label: 'Reel', ratio: '9:16', value: 9 / 16 },
      { chipLabel: 'Story', id: 'instagram_story', label: 'Story', ratio: '9:16', value: 9 / 16 },
    ],
    id: 'instagram',
    label: 'Instagram',
  },
  {
    formats: [
      { chipLabel: 'Portrait', id: 'facebook_feed_portrait', label: 'Feed Portrait', ratio: '4:5', value: 4 / 5 },
      { chipLabel: 'Square', id: 'facebook_feed_square', label: 'Feed Square', ratio: '1:1', value: 1 },
      {
        chipLabel: 'Landscape',
        id: 'facebook_feed_landscape',
        label: 'Feed Landscape',
        ratio: '1.91:1',
        value: 1.91,
      },
      { chipLabel: 'Story', id: 'facebook_stories', label: 'Story', ratio: '9:16', value: 9 / 16 },
      { chipLabel: 'Reel', id: 'facebook_reels', label: 'Reel', ratio: '9:16', value: 9 / 16 },
      {
        chipLabel: 'In-stream video',
        id: 'facebook_in_stream_video',
        label: 'In-Stream Video',
        ratio: '16:9',
        value: 16 / 9,
      },
    ],
    id: 'facebook',
    label: 'Facebook',
  },
  {
    formats: [
      { chipLabel: 'Vertical', id: 'tiktok_standard', label: 'Standard', ratio: '9:16', value: 9 / 16 },
      { chipLabel: 'Square', id: 'tiktok_square', label: 'Square', ratio: '1:1', value: 1 },
      { chipLabel: 'Landscape', id: 'tiktok_landscape', label: 'Landscape', ratio: '16:9', value: 16 / 9 },
    ],
    id: 'tiktok',
    label: 'TikTok',
  },
  {
    formats: [
      { chipLabel: 'Story', id: 'snapchat_story', label: 'Story', ratio: '9:16', value: 9 / 16 },
      { chipLabel: 'Spotlight', id: 'snapchat_spotlight', label: 'Spotlight', ratio: '9:16', value: 9 / 16 },
    ],
    id: 'snapchat',
    label: 'Snap',
  },
  {
    formats: [
      { chipLabel: 'Video', id: 'youtube_standard_video', label: 'Standard Video', ratio: '16:9', value: 16 / 9 },
      { chipLabel: 'Shorts', id: 'youtube_shorts', label: 'Shorts', ratio: '9:16', value: 9 / 16 },
      { chipLabel: 'Thumbnail', id: 'youtube_thumbnail', label: 'Thumbnail', ratio: '16:9', value: 16 / 9 },
    ],
    id: 'youtube',
    label: 'YouTube',
  },
  {
    formats: [
      { chipLabel: 'Portrait', id: 'linkedin_feed_portrait', label: 'Feed Portrait', ratio: '4:5', value: 4 / 5 },
      { chipLabel: 'Square', id: 'linkedin_feed_square', label: 'Feed Square', ratio: '1:1', value: 1 },
      {
        chipLabel: 'Landscape',
        id: 'linkedin_feed_landscape',
        label: 'Feed Landscape',
        ratio: '1.91:1',
        value: 1.91,
      },
      { chipLabel: 'Video', id: 'linkedin_video_vertical', label: 'Video Vertical', ratio: '9:16', value: 9 / 16 },
      { chipLabel: 'Cover', id: 'linkedin_cover_banner', label: 'Cover / Banner', ratio: '4:1', value: 4 },
    ],
    id: 'linkedin',
    label: 'LinkedIn',
  },
  {
    formats: [
      { chipLabel: 'Landscape', id: 'x_single_image', label: 'Single Image', ratio: '16:9', value: 16 / 9 },
      { chipLabel: 'Square', id: 'x_in_stream_square', label: 'In-Stream Square', ratio: '1:1', value: 1 },
      {
        chipLabel: 'Link preview',
        id: 'x_in_stream_landscape',
        label: 'In-Stream Landscape',
        ratio: '1.91:1',
        value: 1.91,
      },
      { chipLabel: 'Header', id: 'x_header_banner', label: 'Header / Banner', ratio: '3:1', value: 3 },
    ],
    id: 'x',
    label: 'X (Twitter)',
  },
];

/**
 * Image-only placements (cover photos, thumbnails, banners). Excluded from video smart crop —
 * the aspect ratio could still crop a video, but these surfaces don't accept video.
 */
export const IMAGE_ONLY_SOCIAL_MEDIA_FORMAT_IDS = new Set([
  'linkedin_cover_banner',
  'x_header_banner',
  'x_single_image',
  'youtube_thumbnail',
]);

/** Platform groups with image-only formats removed (and empty groups dropped). */
export const getVideoCompatiblePlatformGroups = (): SocialMediaPlatformGroup[] =>
  SOCIAL_MEDIA_PLATFORM_GROUPS.map((group) => ({
    ...group,
    formats: group.formats.filter((format) => !IMAGE_ONLY_SOCIAL_MEDIA_FORMAT_IDS.has(format.id)),
  })).filter((group) => group.formats.length > 0);

/** Every platform format id across every group, for classifying a format id as platform vs. custom. */
export const ALL_SOCIAL_MEDIA_FORMAT_IDS = new Set(
  SOCIAL_MEDIA_PLATFORM_GROUPS.flatMap((group) => group.formats.map((format) => format.id)),
);
